// ============================================================
// IMAGEN — una foto suelta como SECCIÓN.
//
// El hueco que llena: ningún otro bloque del builder es solo-imagen. Todos emiten al menos un
// nodo de texto —el campo puede quedar vacío, pero el <h2>/<span> viaja igual al HTML publicado—,
// así que "una imagen y nada más" no se podía hacer. Los dos parecidos se quedan cortos por
// motivos concretos, y conviene tenerlos claros antes de "unificarlos":
//   · `banner` v2 pinta la foto como FONDO CSS (`data-unsplash-bg`), y ese camino NO emite `alt`
//     en ningún punto de editor/unsplash.js: sin texto alternativo, sin `loading`/`decoding` y
//     sin poder participar del preload. Además su <h2> se emite siempre. Aquí la foto es un
//     <img> de verdad, que es justamente la diferencia.
//   · `carrusel` con una sola diapositiva funcionaría, pero arrastra Splide, flechas, puntos,
//     autoplay y las pestañas del editor, y es TIPOS_HERO (se lleva el <h1> y entra en el ritmo
//     de página). Demasiado para una foto a media página.
//
// El BOTÓN es opcional de verdad: sin texto no se emite ancla ninguna (ni en el editor), solo
// una píldora `.editor-only` para añadirlo. Nada de un <a> vacío permanente.
// ============================================================
import { safe } from '../utils/contrast.js';
import { atributoDeBoton } from '../utils/boton-estilo.js';
import { ctaAnchorAttrs } from '../utils/links.js';
import { conSecundario } from '../utils/boton-secundario.js';
import { altAttr } from '../utils/alt-text.js';
import { posSegura } from '../utils/posicion.js';
import { dimensionesDe, atributosDimension, asasCaja } from '../utils/dimension.js';
import { admiteRecorte, clasesImagenRecorte } from '../utils/imagen-recorte.js';
import { atributosEncuadre } from '../utils/encuadre.js';

// ── SSOT de los ajustes del bloque (los consume también editor/tools.js) ──
/** Alturas ofrecidas al cliente. El id viaja en `data-altura` y el CSS lo traduce. */
export const IMAGEN_ALTURAS = [
    { id: 'auto', label: 'Auto' },
    { id: 'compacto', label: 'Compacto' },
    { id: 'medio', label: 'Medio' },
    { id: 'alto', label: 'Alto' },
];
const _ALTURA_IDS = IMAGEN_ALTURAS.map(a => a.id);

/**
 * Altura válida. Default `auto`: manda la PROPORCIÓN REAL de la foto, que es lo que espera
 * quien coloca "una imagen suelta" — recortarla a una banda es una decisión, no el punto
 * de partida.
 */
export const clampAlturaImagen = (v) => {
    const s = String(v == null ? '' : v).trim().toLowerCase();
    return _ALTURA_IDS.indexOf(s) >= 0 ? s : 'auto';
};

/** Texto de arranque del botón. NO es copy del producto: nace editable y el cliente lo cambia. */
export const IMAGEN_CTA_INICIAL = 'Saber más';

const PLACEHOLDER = 'https://placehold.co/1600x900/cccccc/999999?text=Imagen';

// ¿`image` es una KEYWORD de Unsplash por resolver y no una URL/ruta/data: ya lista?
// Espejo de `editor/unsplash.js::isResolved` (un bloque no puede importar de js/editor/:
// rompería la cadena registry→bloque, gotcha nº3). Si cambias una, cambia la otra.
const esKeyword = (v) => !!v && !v.startsWith('http') && !v.startsWith('/') && !v.startsWith('data:');

export const imagenBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const altura = clampAlturaImagen(p.altura);
    // Redimensionado manual: SOLO la v2 (contenida). La v1 va a SANGRE — su ancho es la
    // pantalla, y estrecharla la convertiría en otra variante. La lista vive en
    // utils/dimension.js (SSOT compartida con el editor).
    const dim = dimensionesDe('imagen', variant);
    const dimAttrs = atributosDimension(p, dim);   // {clases, estilo}, una sola pasada
    // "¿Hay botón?" se decide sobre el TEXTO PLANO, no sobre el innerHTML crudo. El extractor
    // persiste `el.innerHTML.trim()` y un contenteditable vaciado en Chrome deja un `<br>`: con
    // la comprobación ingenua, `cta` valdría "<br>" (truthy) y el botón quedaba vivo PARA
    // SIEMPRE — la píldora "Añadir botón" no volvía nunca y el publicado se llevaba un botón con
    // un salto de línea y href="#". Vaciar el texto tiene que poder deshacer el botón.
    const ctaCrudo = typeof p.cta === 'string' ? p.cta : '';
    const ctaPlano = ctaCrudo.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    const cta = ctaPlano ? ctaCrudo : '';

    // La URL sale del MISMO par de props que hero/content (`image_url` → `image_keyword`), que es
    // exactamente lo que escribe `catalog.js::aplicarImagenAlBloque` en su rama de imagen única.
    const imagen = p.image_url || p.image_keyword || '';
    const keyword = esKeyword(imagen);
    const src = keyword ? PLACEHOLDER : (imagen || PLACEHOLDER);
    // `unsplash-target` es lo que hace que editor/unsplash.js hidrate una keyword; una URL ya
    // resuelta no lo necesita y se hornea directa (mismo criterio que gallery/carrusel).
    // Estilo «Recorte» y encuadre (D-1452: «a sangre o en marco: encuadre y recorte»), los dos con
    // la regla de content (utils/imagen-recorte.js, utils/encuadre.js). Sin prop, nada.
    const conFoto = admiteRecorte('imagen', p);
    const enc = conFoto ? atributosEncuadre(p) : { clase: '', estilo: '' };
    const clase = (keyword ? 'imagen-img unsplash-target' : 'imagen-img') + (conFoto ? clasesImagenRecorte(p) : '') + enc.clase;

    // ── LCP: el <img> y el `<link rel=preload>` tienen que DECIR LO MISMO ──
    // `views.py::_build_seo_head` publica la foto del bloque que ABRE la página en un
    // `<link rel="preload" as="image" fetchpriority="high">`. Un `loading="lazy"` en ESA misma
    // <img> es la orden CONTRARIA: el navegador se baja la foto por el preload y luego no la
    // usa hasta que entra en el viewport (avisa en consola de "preload no utilizado"), así que
    // se paga el ancho de banda y no se gana el pintado. Antes este bloque emitía `lazy`
    // SIEMPRE y sin `fetchpriority`, con lo que la contradicción era segura en cuanto la
    // página abría con una imagen.
    // LA REGLA ES LA MISMA EN LOS DOS LADOS y es POSICIONAL para poder serlo: "abre la página"
    // = posiciones [0] o [1] del array de bloques ([0]=header por la regla de estructura del
    // prompt; [1] es el primer contenido, y una página sin header cabe en [0]). Si cambias el
    // criterio aquí, cámbialo en `_build_seo_head` (busca `_LCP_MAX_POS`).
    // Y el mismo matiz que hero.js/carrusel.js: prioridad alta SOLO con la URL ya resuelta —
    // una keyword sin hidratar pinta un placeholder y marcarlo alto no adelanta nada.
    const abrePagina = index <= 1;
    const carga = abrePagina ? '' : ' loading="lazy"';
    const prio = (abrePagina && !keyword) ? ' fetchpriority="high"' : '';

    const img = `<img class="${clase}" src="${src}"`
        + `${imagen ? ` data-unsplash="${imagen}"` : ''} `
        + `${altAttr(p.image_alt, p.cta, p.image_keyword)}`
        + `${carga}${prio} decoding="async"`
        + ` style="opacity:${keyword ? '0' : '1'};${enc.estilo}">`;

    // BOTÓN. Con texto → ancla real por el embudo único de CTAs (utils/links.js): enlace propio
    // → destino global del sitio → '#'. Sin texto → NADA en el publicado y una píldora en el
    // editor para añadirlo (la pinta siempre el render: `compileSiteHtml` corre con editorMode
    // en true y limpia por `.editor-only`, así que emitir de más es seguro y emitir de menos no
    // — corolario del gotcha nº12).
    // POSICIÓN del botón sobre la foto: `{x,y}` en % del marco, el MISMO dato tanto si vino de
    // arrastrar libre como del imán a la rejilla (utils/posicion.js). Viaja en variables CSS y
    // NO como `left/top` inline a propósito: así la media query de móvil puede recentrarlo en
    // horizontal — un `left` inline le ganaría a cualquier regla.
    const pos = posSegura(p.cta_pos);

    const accion = cta
        ? `<div class="imagen-cta-wrap" style="--pos-x:${pos.x}%; --pos-y:${pos.y}%;">
                ${conSecundario(`<a ${ctaAnchorAttrs(p.cta_url)} class="btn-action imagen-cta" data-field="cta" contenteditable="true"${atributoDeBoton(p, 'cta')}>${cta}</a>`, p, { clase: 'btn-action btn-secundario imagen-cta' })}
                <button class="imagen-cta-asa editor-only" type="button" title="Arrastrar para colocarlo (mantén Mayús para encajarlo en la rejilla)" aria-label="Mover el botón">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>
                </button>
                <button class="imagen-cta-quitar editor-only" type="button" title="Quitar el botón" aria-label="Quitar el botón">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line></svg>
                </button>
           </div>`
        : `<button class="imagen-add-cta editor-only" type="button" title="Añadir un botón sobre la imagen">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                <span>Añadir botón</span>
           </button>`;

    // El <section> NO declara `padding:Npx` inline a propósito (igual que hero y carrusel):
    // render.js reescribe ese padding a `var(--section-pad-y, Npx)` y en la v1, que va a sangre,
    // eso abriría bandas de color por encima y por debajo de la foto. El aire de la v2 lo pone
    // su propia regla CSS.
    // CONTRATO DE MARKUP DEL MOTOR DE DIMENSIÓN en modo 'marco' (ver utils/dimension.js): aquí no
    // hay fila que repartir ni divisor — el marco ES la caja, centrada en la sección, y su ancho
    // (`media_width`, variable `--media-w`) se mide contra la sección. Por eso `.imagen-marco`
    // lleva las DOS clases del contrato. Y se queda con las asas de ANCHO (e/w) y sin las de
    // alto: éste lo gobierna el selector de altura del bloque (`data-altura`), no el eje Y del
    // gesto — las filtra `asasCaja(dim)` a partir de la ficha, el render no enumera nada.
    // (El comentario va AQUÍ y no dentro de la plantilla: un acento grave dentro de un template
    // literal lo CIERRA.)
    return `
    <section id="bloque-imagen-${index}" class="reveal imagen-section variant-${variant}${dimAttrs.clases}" data-altura="${altura}"
             style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}';${dimAttrs.estilo}">
        <div class="imagen-marco${dim ? ' n3i-media n3i-caja' : ''}">
            ${img}
            ${accion}
            ${dim ? asasCaja(dim) : ''}
        </div>
    </section>`;
};
